import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { firebaseAuth } from '@/lib/firebase';
import { PageSpinner } from '@/components/ui';
import { Card, CardContent } from '@/components/ui';
import { AlertCircle } from 'lucide-react';

export function AuthCallbackPage() {
  const [error, setError] = useState<string | null>(null);
  const { completeSignInWithEmailLink } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      const url = window.location.href;

      // Check if this is a sign-in link
      if (!firebaseAuth.isSignInWithEmailLink(url)) {
        setError('Invalid sign-in link');
        return;
      }

      try {
        // Complete sign-in (email is retrieved from localStorage)
        await completeSignInWithEmailLink(url);
        // Redirect to home on success
        navigate('/', { replace: true });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Sign-in failed';
        setError(message);
      }
    };

    handleCallback();
  }, [completeSignInWithEmailLink, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)] p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardContent className="py-8 text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
              <h2 className="mt-4 text-xl font-semibold">Sign-in failed</h2>
              <p className="mt-2 text-[var(--color-muted)]">{error}</p>
              <p className="mt-4 text-sm text-[var(--color-muted)]">
                Please try signing in again.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="mt-6 text-sm text-accent-primary hover:underline"
              >
                Go to sign in
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)] p-4">
      <div className="text-center">
        <PageSpinner />
        <p className="mt-4 text-[var(--color-muted)]">Signing you in...</p>
      </div>
    </div>
  );
}
